import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Alert,
    CircularProgress,
    useTheme,
} from '@mui/material';
import {
    AutoAwesome as AutoAwesomeIcon,
    Google as GoogleIcon,
} from '@mui/icons-material';

// Use the exported auth from shared firebase module
import { auth } from '../firebase';
import { signInWithPopup, GoogleAuthProvider, onAuthStateChanged } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Login page component (Firebase Google Auth)
// ---------------------------------------------------------------------------
export function LoginPage() {
    const theme = useTheme();
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Check for redirectTo in query params
    const queryParams = new URLSearchParams(location.search);
    const redirectTo = queryParams.get('redirectTo') || '/app';

    const handleGoogleSignIn = async () => {
        setIsLoading(true);
        setError('');
        try {
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
            // On success, redirect to the intended destination
            navigate(redirectTo);
        } catch (err) {
            setError(err.message || 'Failed to sign in with Google');
            setIsLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #0f0c29, #302b63, #24243e)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                p: 3,
            }}
        >
            <Card
                sx={{
                    maxWidth: 420,
                    width: '100%',
                    borderRadius: 4,
                    backdropFilter: 'blur(20px)',
                    background: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(255,255,255,0.9)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    boxShadow: '0 32px 64px -12px rgba(0,0,0,0.4)',
                }}
            >
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Box
                            sx={{
                                width: 64,
                                height: 64,
                                borderRadius: '18px',
                                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2,
                                boxShadow: '0 12px 24px -4px rgba(99,102,241,0.4)',
                            }}
                        >
                            <AutoAwesomeIcon sx={{ color: 'white', fontSize: 32 }} />
                        </Box>
                        <Typography variant="h5" fontWeight={800} gutterBottom>
                            Social Media Hub
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Sign in to manage your empire securely
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Button
                        onClick={handleGoogleSignIn}
                        disabled={isLoading}
                        fullWidth
                        variant="contained"
                        size="large"
                        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
                        sx={{
                            py: 1.5,
                            borderRadius: 3,
                            background: '#ffffff',
                            color: '#000000',
                            fontWeight: 700,
                            fontSize: '1rem',
                            boxShadow: '0 8px 20px -4px rgba(0,0,0,0.2)',
                            '&:hover': {
                                background: '#f1f1f1',
                            },
                        }}
                    >
                        {isLoading ? 'Signing In...' : 'Sign In with Google'}
                    </Button>
                </CardContent>
            </Card>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// RequireAuth wrapper — gates any component behind the Firebase auth check
//
// E2E bypass rules:
//   ACTIVE  — Playwright injects `window.__E2E_MOCK_AUTH__ = true` via addInitScript().
//             This is ephemeral (per page-load) so it never bleeds into normal sessions.
//   INACTIVE — env var alone, URL params, or stale localStorage do NOT trigger bypass.
//             Normal localhost dev always uses real Firebase auth.
// ---------------------------------------------------------------------------
export function RequireAuth({ children }) {
    // Safety guard: bypass is only even possible when the build explicitly permits it.
    // In production this env var is never set, so the bypass is completely dead code.
    const bypassPermitted = process.env.REACT_APP_ENABLE_MOCK_AUTH === 'true';

    // The ONLY trigger: Playwright's addInitScript sets this on the window object.
    // window flags are ephemeral — they disappear on every page load/reload.
    const isE2E = bypassPermitted &&
        typeof window !== 'undefined' &&
        window.__E2E_MOCK_AUTH__ === true;

    const mockUser = isE2E ? {
        uid: 'e2e-mock-user',
        email: 'e2e@test.com',
        displayName: 'E2E Tester',
    } : null;

    const [user, setUser] = useState(mockUser);
    const [loading, setLoading] = useState(!isE2E);

    useEffect(() => {
        if (isE2E) {
            // E2E mode: skip Firebase entirely
            return;
        }

        // Normal flow: subscribe to Firebase auth state
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setLoading(false);
        });
        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return <LoginPage />;
    }

    return children;
}

export default RequireAuth;
